class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.body = document.querySelector("body");
    this.header = document.querySelector(".shopify-section-header");
    this.headerBottom = document.querySelector(".header__bottom");
    this.promoModal = document.querySelector(".search__modal");
    this.collectionList = document.querySelector(".template-404__collections");
    this.headerInput = document.querySelector(".header__search .search__input");
    this.setupEventListeners();
  }

  calcHeight() {
    if (document.querySelector(".predictive-search")) {
      const searchAllTabs = document.querySelectorAll(
        ".predictive-search__results-list:not(.predictive-search__results-list_pages)"
      );
      let allGridsOnOneLine = true;

      searchAllTabs.forEach((grid) => {
        const children = [...grid.children];
        const computedStyle = getComputedStyle(grid);
        const columns = computedStyle.gridTemplateColumns
          .split(/\s+(?![^(]*\))/)
          .filter(Boolean).length;
        if (children.length > columns) allGridsOnOneLine = false;
      });

      if (!allGridsOnOneLine) {
        document.documentElement.style.removeProperty("--search-height");
        return;
      }

      let maxHeight = 0;
      searchAllTabs.forEach((div) => {
        if (div.offsetHeight > maxHeight) maxHeight = div.offsetHeight;
      });
      document.documentElement.style.setProperty("--search-height", `${maxHeight + 2}px`);
    }
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    window.addEventListener("resize", () => {
      setTimeout(() => this.calcHeight(), 100);
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
    if (this.getQuery().length) this.hidePromoBlock();
    else this.showPromoBlock();
  }

  onFormSubmit(event) {
    const submitter = event.submitter;
    const selectedResult = this.querySelector('[aria-selected="true"] a');
    const isCardLink = submitter?.classList?.contains("card__link");

    if (!this.getQuery().length || selectedResult || isCardLink) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (this.classList.contains("search-modal__form")) {
      this.headerBottom?.classList.add("header__bottom--visible");
      this.promoModal?.classList.remove("search__modal--hidden");
      this.collectionList?.classList.remove("hidden");
    }

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
      this.promoModal?.classList.add("search__modal--hidden");
      this.collectionList?.classList.add("hidden");
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if (this.classList.contains("search-modal__form")) {
      this.headerBottom?.classList.remove("header__bottom--visible");
    }
    if (!this.classList.contains("search-modal__form")) {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close(true);
      });
    }
  }

  onBlur() {
    this.headerInput?.blur();
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case "Enter":
        event.preventDefault();
        this.selectOption();
        break;
      case "Escape":
        event.preventDefault();
        this.close(true);
        this.showPromoBlock();
        break;
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector("[data-predictive-search-search-for-text]");
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      const matches = [...currentButtonText.matchAll(new RegExp(previousTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"))];
      if (matches.length <= 1) {
        searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
      }
    }
    if (this.getQuery().length) this.hidePromoBlock();
    else this.showPromoBlock();
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.trim().replace(/\s+/g, "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.clickSearchTabs();
      return;
    }

    if (this.promoModal && this.classList.contains("search-modal__form")) {
      this.promoModal.classList.add("search__modal--hidden");
    }
    this.collectionList?.classList.add("hidden");

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          this.close();
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const section = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search");

        if (!section) throw new Error("Predictive search section was not returned");
        const resultsMarkup = section.innerHTML;

        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
        this.clickSearchTabs();

        try { colorSwatches(); } catch (err) {}
      })
      .catch((error) => {
        if (error?.name === "AbortError" || error?.code === 20) return;
        this.close();
        console.error("Predictive search error", error);
      });

    if (this.getQuery().length) this.hidePromoBlock();
    else this.showPromoBlock();
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");
    if (!this.statusElement) return;
    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText || "";
    setTimeout(() => this.statusElement?.setAttribute("aria-hidden", "true"), 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    this.setLiveRegionResults();
    this.open();
    if (this.promoModal && this.classList.contains("search-modal__form")) {
      this.promoModal.classList.add("search__modal--hidden");
    }
    this.collectionList?.classList.add("hidden");
    if (this.querySelector(".predictive-search__results-list_products")?.dataset.on_hover_slider) {
      initSlider();
    }
    setTimeout(() => this.calcHeight());
  }

  clickSearchTabs() {
    const results = this.querySelectorAll(".predictive-search__results-list");
    const tabs = this.querySelectorAll(".predictive-search__result-tab");
    if (!results.length || !tabs.length) return;

    const activeTab = this.querySelector(".predictive-search__result-tab.active") || tabs[0];
    const activeType = activeTab.dataset.typeTarget;
    tabs.forEach((tab) => tab.classList.toggle("active", tab === activeTab));
    results.forEach((result) => result.classList.toggle("active", result.dataset.type === activeType));

    tabs.forEach((tab) => {
      tab.addEventListener("click", (event) => {
        event.preventDefault();
        const typeTarget = tab.dataset.typeTarget;
        tabs.forEach((element) => element.classList.remove("active"));
        tab.classList.add("active");
        results.forEach((element) => {
          element.classList.toggle("active", element.dataset.type === typeTarget);
        });
        setTimeout(() => this.calcHeight());
      });
    });
  }

  hidePromoBlock() {
    if (this.classList.contains("search-modal__form")) {
      this.promoModal?.classList.add("search__modal--hidden");
      this.collectionList?.classList.add("hidden");
    }
  }

  showPromoBlock() {
    if (this.classList.contains("search-modal__form")) {
      this.promoModal?.classList.remove("search__modal--hidden");
      this.collectionList?.classList.remove("hidden");
    }
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const count = this.querySelector("[data-predictive-search-live-region-count-value]");
    if (count) this.setLiveRegionText(count.textContent);
  }

  open() {
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    if (this.getQuery().length) this.hidePromoBlock();
    else this.showPromoBlock();
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.searchTerm = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute("aria-selected", "false");
    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.predictiveSearchResults?.removeAttribute("style");
    if (this.getQuery().length) this.hidePromoBlock();
    else this.showPromoBlock();
  }
}

customElements.define("predictive-search", PredictiveSearch);
